
import * as crudModels from "~/models";
import { applyCollectionQueryState } from "../../filters/collectionQueryState";
import stickyCollectionFilters from "../../mix-ins/stickyCollectionFilters";

const resetQuery = (vm) => {
  vm.layout.collection.clearFiltersExcept([...vm.queryStringFilters]);

  // Only clear dynamic filters against an ACTUAL incoming route query.
  // A genuinely empty query means "no navigational state at all", not "an
  // explicit state with no df__" -- clearing here regardless would wipe a
  // layout's constructor-seeded defaultFilters (asana-995, Task 7) on
  // every mount, since this runs unconditionally from mounted().
  if (Object.keys(vm.$route.query).length) {
    const dynamicFilters = vm.layout.collection.dynamicFilterController;
    if (!vm.queryStringFilters.includes(dynamicFilters.queryName)) {
      dynamicFilters.clear();
    }
  }

  vm.layout.collection.resetQueryOptionsExcept(
    vm.currentOptionKeys.map((queryName) => queryName.substr(1))
  );
  vm.layout.collection.applyStagedFilters();
};

export default {
  mixins: [stickyCollectionFilters],
  data() {
    const layout = crudModels[this.modelName].getCollectionLayout(
      this.layoutId
    );

    return {
      listTitle: crudModels[this.modelName].listTitle,
      icon: crudModels[this.modelName].icon,
      layout,
    };
  },
  props: {
    modelName: {
      type: String,
      required: true,
    },
    layoutId: {
      type: String,
    },
  },
  computed: {
    currentOptionKeys() {
      return Object.keys(this.$route.query).filter(
        (queryName) => queryName.indexOf("_") === 0
      );
    },
    queryStringFilters() {
      return Object.keys(this.$route.query)
        .filter((queryName) => queryName.indexOf("_") !== 0)
        .filter((queryName) => this.layout.collection.getFilter(queryName));
    },
  },
  watch: {
    "layout.collection.filterQueryValues": {
      handler() {
        const setFilters = _.pickBy(
          this.layout.collection.filterQueryValues,
          (value) => value || value === 0
        );

        const queryFiltersString = new URLSearchParams(setFilters).toString();
        const existingQueryOptionsString = new URLSearchParams(
          _.pick(this.$route.query, this.currentOptionKeys)
        ).toString();

        const allQueryStrings = [
          queryFiltersString,
          existingQueryOptionsString,
        ].filter((s) => s.length > 0);

        const newStateUrl =
          this.$route.path +
          (allQueryStrings.length > 0 ? "?" + allQueryStrings.join("&") : "");

        const curUrl = new URL(document.location.href);
        const curStateUrl = curUrl.pathname + curUrl.search;

        if (curStateUrl != newStateUrl) {
          history.pushState({}, "", newStateUrl);
        }

        // persistStickyState() is NOT called here -- the mixin's own
        // `filterQueryValues` watcher already does, and calling it from
        // both would just double-invoke it on every filter change.
      },
    },
    "$route.query": {
      handler() {
        // An empty query is not "an explicit state that clears every
        // filter" -- it is "no incoming navigational state at all" (a
        // locally-initiated filter change never touches $route; it writes
        // the URL via history.pushState directly, in the watcher below, so
        // Vue Router never reactively sees it). Applying it here would wipe
        // a layout's constructor-seeded defaultFilters (asana-995, Task 7)
        // the moment this immediate watcher fires on mount.
        if (!Object.keys(this.$route.query).length) return;

        applyCollectionQueryState(
          this.layout.collection,
          this.$route.query
        );
      },
      immediate: true,
    },
  },
  methods: {
    // Router-aware override of the mixin's non-router default -- a sticky
    // restore stays shareable and the back button keeps working.
    loadQueryState(state) {
      this.layout.collection.clearFiltersExcept([]);
      this.layout.collection.dynamicFilterController.clear();
      this.layout.collection.resetQueryOptionsExcept([]);
      this.layout.collection.applyStagedFilters();

      return this.$router
        .replace({
          path: this.$route.path,
          query: { ...state },
        })
        .catch((error) => {
          if (error.name !== "NavigationDuplicated") throw error;
        });
    },
  },
  beforeRouteUpdate(to, from, next) {
    setTimeout(() => resetQuery(this), 100);
    next();
  },
  mounted() {
    resetQuery(this);
    this.initStickyFilters();
  },
};
